import { CommonModule } from '@angular/common';
import { faviconForHost } from './app.utils';
import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Actions, ofActionDispatched, Store } from '@ngxs/store';
import {
  AppInitDone,
  AuthenticationSelectors,
  AuthenticationService,
  LanguageInfo,
  LoginApplicationTest,
  LoginSuccessDomain,
  SetLoading,
  SetUserRightsSuccess,
} from '@orion-platform/core';
import { LoginResponse } from 'angular-auth-oidc-client';
import { DashboardNotifierComponent } from 'libs/core/src/lib/components/dashboard-notifier/dashboard-notifier.component';
import { SignalrNotificationComponent } from 'libs/core/src/lib/components/signalr-notification/signalr-notification.component';
import { SetupLanguage } from 'libs/core/src/lib/language';
import { merge } from 'rxjs';
import { filter } from 'rxjs/operators';
import { isUrlAccessible, resolveLandingUrl } from './models/route-access';
import { SidebarComponent } from './protected/sidebar/sidebar.component';
import { ToolbarComponent } from './protected/toolbar/toolbar.component';
import { getActiveLanguage } from './utils/general';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    RouterOutlet,
    SidebarComponent,
    ToolbarComponent,
    SignalrNotificationComponent,
    DashboardNotifierComponent,
  ],
  selector: 'app-root',
  styleUrl: './app.scss',
  templateUrl: './app.html',
})
export class App implements OnInit {
  private destroyRef = inject(DestroyRef);
  private authService = inject(AuthenticationService);
  private translate = inject(TranslateService);
  private store = inject(Store);
  private actions = inject(Actions);
  private router = inject(Router);

  showToolbar = signal<boolean>(false);
  showSidebar = signal<boolean>(false);
  isSidebarCollapse = true;
  showNotification = signal<boolean>(false);

  readonly SIDEBAR_COLLAPSE_WIDTH = 7;
  sidebarWidth = 0;
  mainWindowWidth = 100;

  constructor() {}

  ngOnInit(): void {
    this.store.dispatch(new SetLoading(true));
    this.authService.getLoginStatus().subscribe((isLogin): void => {
      this.showNotification.set(isLogin);
    });
    this.authService.getIsAuthorized().subscribe((authenticatedResult): void => {
      this.showSidebar.set(authenticatedResult?.isAuthenticated);
      this.sidebarWidth = authenticatedResult?.isAuthenticated ? this.SIDEBAR_COLLAPSE_WIDTH : 0;
      this.mainWindowWidth = authenticatedResult?.isAuthenticated
        ? 100 - this.SIDEBAR_COLLAPSE_WIDTH
        : 100;
    });
    this.router.events.subscribe((event): void => {
      if (
        event instanceof NavigationStart &&
        this.authService.getCancellationToken() &&
        this.authService.isAuthInitialized()
      ) {
        const { url } = event;
        if (url !== '/dashboard') this.authService.invokeCancellationToken();
      }
      if (event instanceof NavigationEnd) {
        const finalUrl = event.urlAfterRedirects;
        this.showToolbar.set(!['/', '/login', '/home'].includes(finalUrl));
      }
    });
    this.actions
      .pipe(ofActionDispatched(LoginSuccessDomain, LoginApplicationTest))
      .subscribe((): void => {
        this.router.navigate(['/dashboard']);
      });
    this.setAppLanguage();
    this.authenticationHandler();
    this.changeFaviconBasedOnEnvironment();
    this.setupRouteAccessGuard();
  }

  /**
   * Updates the favicon to the one this environment wears.
   *
   * Decided from the host the app is served from, because the build cannot say:
   * every environment runs the same image, built the same way, and differs only
   * by the configuration mounted into it.
   *
   * No-op where the host is not a named environment, or the document carries no
   * icon link to point somewhere else.
   */
  changeFaviconBasedOnEnvironment(): void {
    const faviconFile = faviconForHost(window.location.hostname);
    if (!faviconFile) return;

    const link: HTMLLinkElement | null =
      document.querySelector<HTMLLinkElement>("link[rel*='icon']");
    if (!link) return;

    const currentHref = link.getAttribute('href') ?? '';
    const newHref = currentHref.includes('favicon.ico')
      ? currentHref.replace('favicon.ico', faviconFile)
      : faviconFile;

    link.setAttribute('href', newHref);
  }

  /**
   * Handles the authentication flow during application initialization.
   *
   * - Calls a single-shot authentication check (`checkAuthOnce`)
   *   which resolves the redirect callback and determines the final
   *   authenticated state.
   * - While this check is running, the global loading indicator remains `true`.
   */
  private authenticationHandler(): void {
    this.authService
      .checkAuthOnce()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ isAuthenticated }: LoginResponse): void => {
        isAuthenticated
          ? this.store.dispatch(new AppInitDone())
          : this.store.dispatch(new SetLoading(false));
      });
  }

  /**
   * Initializes the application language.
   */
  private setAppLanguage(): void {
    const language: LanguageInfo = getActiveLanguage();

    this.translate.use(language.code);
    this.store.dispatch(new SetupLanguage(language));
  }

  // Redirect away from routes the logged-in user has no permission for — covers the
  // previous-user-page bug after re-login by triggering on both rights load and every NavigationEnd.
  private setupRouteAccessGuard(): void {
    merge(
      // Fires whenever the user's menu rights are (re)computed — login, token refresh, plant switch.
      this.actions.pipe(ofActionDispatched(SetUserRightsSuccess)),
      // Fires after every navigation — catches the post-login redirect that may finish after rights load.
      this.router.events.pipe(filter((event): boolean => event instanceof NavigationEnd)),
    ).subscribe((): void => this.enforceRouteAccess());
  }

  private enforceRouteAccess(): void {
    // Current user's accessible menu items.
    const rights = this.store.selectSnapshot(AuthenticationSelectors.menuAccessRightList) || [];
    // Rights not loaded yet — skip to avoid false redirects in the initial state.
    if (!rights.length) return;
    const widgetRights =
      this.store.selectSnapshot(AuthenticationSelectors.widgetAccessRightList) || [];
    const currentUrl = this.router.url;
    const isDashboard = currentUrl === '/dashboard' || currentUrl.indexOf('/dashboard?') === 0;

    // PO decision: a user with no dashboard widgets should land on their first menu item
    // instead of the empty dashboard. Catches both initial landing and direct nav.
    if (isDashboard && widgetRights.length === 0) {
      const landing = resolveLandingUrl(rights, widgetRights);
      if (landing !== currentUrl) this.router.navigateByUrl(landing);
      return;
    }

    // Public routes and routes not bound to a sidebar item pass through.
    if (isUrlAccessible(currentUrl, rights)) return;
    // /dashboard if user has widgets, else first accessible sidebar item, else /unauthorized.
    const landing = resolveLandingUrl(rights, widgetRights);
    // Already on landing — bail out to prevent navigation loops.
    if (landing === currentUrl) return;
    this.router.navigateByUrl(landing);
  }

  onSidebarHover(): void {
    // this.isSidebarCollapse = false;
  }

  offSidebarHover(): void {
    // this.isSidebarCollapse = true;
  }
}
